import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Button, Chip, 
    CircularProgress, Tab, Tabs, Alert, Dialog, 
    DialogActions, DialogContent, DialogContentText, 
    DialogTitle, Snackbar, IconButton, Tooltip, Avatar, Container, Stack
} from '@mui/material';

// İkonlar
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArticleIcon from '@mui/icons-material/Article'; // Blog ikonu
import MapIcon from '@mui/icons-material/Map'; // Rota ikonu
import InboxIcon from '@mui/icons-material/Inbox'; // Boş durum ikonu
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // Modal uyarı ikonu

// API URL
const API_URL = process.env.REACT_APP_API_URL;

const AdminOnayPaneli = () => {
    // --- State Tanımları ---
    const [tabValue, setTabValue] = useState(0); 
    const [bekleyenRotalar, setBekleyenRotalar] = useState([]);
    const [bekleyenBloglar, setBekleyenBloglar] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal & Snackbar State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogData, setDialogData] = useState({ type: '', id: null, isApprove: true });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const token = localStorage.getItem('token'); 

    // --- Veri Çekme ---
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            
            // Promise.all ile paralel istek (Daha hızlı yükleme için)
            const [rotalarRes, bloglarRes] = await Promise.allSettled([
                axios.get(`${API_URL}/admin/pending-routes`, config),
                axios.get(`${API_URL}/admin/pending-blogs`, config)
            ]);

            if (rotalarRes.status === 'fulfilled') setBekleyenRotalar(rotalarRes.value.data);
            if (bloglarRes.status === 'fulfilled') setBekleyenBloglar(bloglarRes.value.data);

        } catch (generalError) {
            console.error("Hata:", generalError);
            setError("Sunucu ile iletişim kurulurken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // --- Handlerlar ---
    const handleOpenConfirm = (type, id, isApprove) => {
        setDialogData({ type, id, isApprove });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => setDialogOpen(false);

    const handleProcessConfirm = async () => {
        const { type, id, isApprove } = dialogData;
        handleCloseDialog();

        try {
            await axios.post(`${API_URL}/admin/approve-content`, 
                { type, id, approve: isApprove }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            fetchData();
            setSnackbar({
                open: true,
                message: `İçerik başarıyla ${isApprove ? 'yayınlandı' : 'reddedildi'}.`,
                severity: 'success'
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: "İşlem başarısız: " + (error.response?.data?.detail || error.message),
                severity: 'error'
            });
        }
    };

    // --- Render Helperları ---
    const renderEmptyState = (type) => (
        <TableRow>
            <TableCell colSpan={4} align="center" sx={{ borderBottom: 'none' }}>
                <Box className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <InboxIcon sx={{ fontSize: 60, opacity: 0.5, mb: 2 }} />
                    <Typography variant="h6" className="font-medium">
                        Bekleyen {type === 'rota' ? 'rota' : 'blog'} bulunmuyor
                    </Typography>
                    <Typography variant="body2" className="text-gray-600 mt-1">
                        Şu an onay bekleyen yeni bir içerik yok.
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    const renderTable = (data, type) => (
        <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'transparent', mt: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                    <TableRow sx={{ '& th': { borderBottom: '1px solid #374151', color: '#9ca3af', fontWeight: 600 } }}>
                        <TableCell>İÇERİK BAŞLIĞI</TableCell>
                        <TableCell>YAZAR / GÖNDEREN</TableCell>
                        <TableCell>TARİH</TableCell>
                        <TableCell align="right">İŞLEMLER</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.length === 0 ? renderEmptyState(type) : (
                        data.map((item) => (
                            <TableRow 
                                key={item.id} 
                                sx={{ 
                                    '& td': { borderBottom: '1px solid #1f2937', color: '#e5e7eb' },
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.03)' },
                                    transition: 'background-color 0.2s'
                                }}
                            >
                                <TableCell component="th" scope="row">
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Avatar sx={{ bgcolor: type === 'rota' ? '#f59e0b' : '#3b82f6', width: 32, height: 32 }}>
                                            {type === 'rota' ? <MapIcon fontSize="small"/> : <ArticleIcon fontSize="small"/>}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold" className="text-white">
                                                {item.title || item.baslik}
                                            </Typography>
                                            <Chip label="Onay Bekliyor" size="small" 
                                                sx={{ 
                                                    mt: 0.5, height: 20, fontSize: '0.65rem', 
                                                    bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.3)' 
                                                }} 
                                            />
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-300">
                                            {(item.yazar || item.author || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <span>{item.yazar || item.author || 'Bilinmiyor'}</span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {new Date(item.created_at || item.date || Date.now()).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Tooltip title="Detaylı İncele" arrow>
                                            <IconButton 
                                                size="small"
                                                onClick={() => window.open(`/${type === 'rotalar' ? 'rotalar' : 'blog'}/${item.id}`, '_blank')}
                                                sx={{ color: '#60a5fa', bgcolor: 'rgba(59, 130, 246, 0.1)', '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.2)' } }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Onayla ve Yayınla" arrow>
                                            <IconButton 
                                                size="small"
                                                onClick={() => handleOpenConfirm(type, item.id, true)}
                                                sx={{ color: '#4ade80', bgcolor: 'rgba(74, 222, 128, 0.1)', '&:hover': { bgcolor: 'rgba(74, 222, 128, 0.2)' } }}
                                            >
                                                <CheckCircleIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Reddet" arrow>
                                            <IconButton 
                                                size="small"
                                                onClick={() => handleOpenConfirm(type, item.id, false)}
                                                sx={{ color: '#f87171', bgcolor: 'rgba(248, 113, 113, 0.1)', '&:hover': { bgcolor: 'rgba(248, 113, 113, 0.2)' } }}
                                            >
                                                <CancelIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 p-4 md:p-8">
            <Container maxWidth="lg">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <div>
                        <Typography variant="h4" className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
                            Yönetici Paneli
                        </Typography>
                        <Typography variant="body2" className="text-gray-400 mt-1">
                            Kullanıcılardan gelen içerikleri yönetin.
                        </Typography>
                    </div>
                    <Button 
                        startIcon={<RefreshIcon />} 
                        onClick={fetchData}
                        variant="outlined"
                        sx={{ 
                            color: '#9ca3af', borderColor: '#374151', 
                            '&:hover': { borderColor: '#fbbf24', color: '#fbbf24', bgcolor: 'transparent' } 
                        }}
                    >
                        Listeyi Yenile
                    </Button>
                </div>

                {error && (
                    <Alert severity="error" onClose={() => setError(null)} className="mb-6 bg-red-900/30 text-red-200 border border-red-800">
                        {error}
                    </Alert>
                )}

                {/* Main Content Card */}
                <Paper sx={{ bgcolor: '#111827', border: '1px solid #374151', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ borderBottom: 1, borderColor: '#374151' }}>
                        <Tabs 
                            value={tabValue} 
                            onChange={(e, v) => setTabValue(v)} 
                            textColor="inherit"
                            indicatorColor="secondary"
                            sx={{
                                '& .MuiTab-root': { 
                                    textTransform: 'none', fontWeight: 600, fontSize: '1rem', color: '#6b7280', minHeight: 60 
                                },
                                '& .Mui-selected': { color: '#fbbf24' },
                                '& .MuiTabs-indicator': { backgroundColor: '#fbbf24', height: 3 }
                            }}
                        >
                            <Tab 
                                icon={<MapIcon fontSize="small" />} 
                                iconPosition="start" 
                                label={`Rotalar (${bekleyenRotalar.length})`} 
                            />
                            <Tab 
                                icon={<ArticleIcon fontSize="small" />} 
                                iconPosition="start" 
                                label={`Bloglar (${bekleyenBloglar.length})`} 
                            />
                        </Tabs>
                    </Box>

                    <Box className="p-2 md:p-4 min-h-[400px]">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-64 gap-3">
                                <CircularProgress sx={{ color: '#fbbf24' }} thickness={4} />
                                <span className="text-gray-500 text-sm animate-pulse">Veriler yükleniyor...</span>
                            </div>
                        ) : (
                            <>
                                {tabValue === 0 && renderTable(bekleyenRotalar, 'rotalar')}
                                {tabValue === 1 && renderTable(bekleyenBloglar, 'blog')}
                            </>
                        )}
                    </Box>
                </Paper>
            </Container>

            {/* --- ONAY MODALI (DIALOG) --- */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                PaperProps={{
                    style: { backgroundColor: '#1f2937', color: 'white', border: '1px solid #374151', borderRadius: 12, maxWidth: 450 }
                }}
                slotProps={{
                    backdrop: { style: { backdropFilter: 'blur(3px)' } } // Arka planı bulanıklaştır
                }}
            >
                <div className="p-4 text-center">
                    {dialogData.isApprove ? (
                        <CheckCircleIcon sx={{ fontSize: 60, color: '#4ade80', mb: 2 }} />
                    ) : (
                        <WarningAmberIcon sx={{ fontSize: 60, color: '#f87171', mb: 2 }} />
                    )}
                    
                    <DialogTitle sx={{ p: 0, mb: 1, fontWeight: 'bold', fontSize: '1.25rem' }}>
                        {dialogData.isApprove ? "Yayınlama Onayı" : "Reddetme Onayı"}
                    </DialogTitle>
                    
                    <DialogContent sx={{ p: 0, px: 2, mb: 3 }}>
                        <DialogContentText className="text-gray-400">
                            {dialogData.isApprove 
                                ? "Bu içeriği onaylayarak herkesin görmesini sağlamak üzeresiniz. Emin misiniz?" 
                                : "Bu içeriği reddedeceksiniz. Bu işlem geri alınamaz ve içerik yayından kaldırılır."}
                        </DialogContentText>
                    </DialogContent>

                    <DialogActions sx={{ justifyContent: 'center', gap: 2, p: 0 }}>
                        <Button onClick={handleCloseDialog} variant="outlined" sx={{ color: '#9ca3af', borderColor: '#4b5563', '&:hover': { borderColor: 'white', color: 'white' } }}>
                            Vazgeç
                        </Button>
                        <Button 
                            onClick={handleProcessConfirm} 
                            variant="contained"
                            sx={{ 
                                bgcolor: dialogData.isApprove ? '#16a34a' : '#dc2626',
                                '&:hover': { bgcolor: dialogData.isApprove ? '#15803d' : '#b91c1c' }
                            }}
                            autoFocus
                        >
                            {dialogData.isApprove ? "Onayla ve Yayınla" : "Reddet"}
                        </Button>
                    </DialogActions>
                </div>
            </Dialog>

            {/* --- BİLDİRİM (SNACKBAR) --- */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={4000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity} 
                    variant="filled"
                    sx={{ width: '100%', borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default AdminOnayPaneli;